import { ScenarioLoad } from './load/ScenarioLoad';
import { ScenarioPV } from './pv/ScenarioPV';
import { ScenarioGrid } from './grid/ScenarioGrid';
import { BusinessModelRef, ConfigurationRef, InjectionModelRef } from './ScenarioReference';
import { ScenarioGenerator } from './generator/ScenarioGenerator';
import { ScenarioEco } from './eco/ScenarioEco';
import { ScenarioSizing } from './sizing/ScenarioSizing';
import { ScenarioBattery } from './battery/ScenarioBattery';
import assignToThis from '../helper/assignToThis';
import { ProjectMeta } from '@/classes/ProjectMeta';

const HAS_GRID = 1;
const HAS_GRID24 = 2;
const HAS_INJECTION = 4;
const HAS_GENSET = 8;
const HAS_BATTERY = 16;
const HAS_GENSET_BACKUP = 32;

const CONFIGURATION_BY_KEY: any = {};
CONFIGURATION_BY_KEY['c' + 0] = ConfigurationRef.configuration_off_grid_self_consumption;
CONFIGURATION_BY_KEY['c' + HAS_GENSET] = ConfigurationRef.configuration_off_grid_self_consumption_genset;
CONFIGURATION_BY_KEY['c' + HAS_BATTERY] = ConfigurationRef.configuration_off_grid_self_consumption_battery;
CONFIGURATION_BY_KEY['c' + (HAS_BATTERY + HAS_GENSET)] = ConfigurationRef.configuration_off_grid_self_consumption_battery_genset;
CONFIGURATION_BY_KEY['c' + (HAS_GRID)] = ConfigurationRef.configuration_bad_grid_self_consumption_grid;
CONFIGURATION_BY_KEY['c' + (HAS_GRID + HAS_GENSET)] = ConfigurationRef.configuration_bad_grid_self_consumption_grid_genset;
CONFIGURATION_BY_KEY['c' + (HAS_GRID + HAS_GENSET + HAS_GENSET_BACKUP)] = ConfigurationRef.configuration_bad_grid_self_consumption_grid_genset_backup;
CONFIGURATION_BY_KEY['c' + (HAS_GRID + HAS_BATTERY)] = ConfigurationRef.configuration_bad_grid_self_consumption_grid_battery;
CONFIGURATION_BY_KEY['c' + (HAS_GRID + HAS_GENSET + HAS_BATTERY)] = ConfigurationRef.configuration_bad_grid_self_consumption_grid_battery_genset;
CONFIGURATION_BY_KEY['c' + (HAS_GRID + HAS_GRID24)] = ConfigurationRef.configuration_self_consumption_without_injection_grid_24;
CONFIGURATION_BY_KEY['c' + (HAS_GRID + HAS_GRID24 + HAS_BATTERY)] = ConfigurationRef.configuration_self_consumption_without_injection_grid_24_battery;
CONFIGURATION_BY_KEY['c' + (HAS_GRID + HAS_GRID24 + HAS_INJECTION) + InjectionModelRef.self_consumption_with_injection] = ConfigurationRef.configuration_self_consumption_with_injection_grid_24_injection;
CONFIGURATION_BY_KEY['c' + (HAS_GRID + HAS_GRID24 + HAS_INJECTION) + InjectionModelRef.net_metering] = ConfigurationRef.configuration_net_metering_grid_24_injection;
CONFIGURATION_BY_KEY['c' + (HAS_GRID + HAS_GRID24 + HAS_INJECTION) + InjectionModelRef.total_injection] = ConfigurationRef.configuration_total_injection_grid_24_injection;
CONFIGURATION_BY_KEY['c' + (HAS_GRID + HAS_GRID24 + HAS_INJECTION + HAS_BATTERY) + InjectionModelRef.total_injection] = ConfigurationRef.configuration_total_injection_grid_24_injection_battery;

export class Scenario {

  public static getConfiguration(scenario: Scenario): string | null {
    // we need at least a response to the first step of the wizard to determine a configuration
    if (scenario.hasGrid == null) {
      return null;
    }

    const hasGrid = scenario.hasGrid;
    const hasGrid24 = hasGrid && scenario.grid && scenario.grid.hasGrid24;
    const hasInjection = hasGrid24 && scenario.hasInjection;
    const hasGenset = !hasInjection && !hasGrid24 && scenario.hasGenset;
    const hasBattery = (!hasGrid || !hasGrid24 || !hasInjection) && scenario.hasBattery;
    const hasGeneratorBackup = hasGrid && !hasGrid24 && hasGenset && !hasBattery && scenario.generator && scenario.generator.isGeneratorBackup;

    let mask = 0;
    mask += hasGrid ? HAS_GRID : 0;
    mask += hasGrid24 ? HAS_GRID24 : 0;
    mask += hasInjection ? HAS_INJECTION : 0;
    mask += hasGenset ? HAS_GENSET : 0;
    mask += hasBattery ? HAS_BATTERY : 0;
    mask += hasGeneratorBackup ? HAS_GENSET_BACKUP : 0;
    const key = 'c' + mask + (scenario.grid && scenario.hasInjection && scenario.injectionModelRef ? scenario.injectionModelRef : '');
    // console.log(key);
    // console.log(CONFIGURATION_BY_KEY);
    return CONFIGURATION_BY_KEY[key];
  }
  public project: ProjectMeta | null = null;
  
  public uuid?: string | null = null;
  
  public isRenFrance: boolean = false;

  public name: string | null = null;

  public totalEnergiesShareholdingInterest: number | null = null;

  public durationYear: number | null = null;
  public irc: string | null = null;

  public mocInflation: string | null = null;
  public ircInflation: string | null = null;
  public mocFx: string | null = null;
  public ircFx: string | null = null;
  public regionalFdThreshold: number | null = null;
  public fx: number | null = null;

  public hasGrid: boolean | null = null;
  public hasGenset: boolean | null = null;
  public hasBattery: boolean | null = null;
  public hasInjection: boolean | null = null;

  public businessModelRef: BusinessModelRef | null = null;
  public injectionModelRef: InjectionModelRef | null = null;

  public load: ScenarioLoad | null = null;
  public pv: ScenarioPV | null = null;
  public grid: ScenarioGrid | null = null;
  public generator: ScenarioGenerator | null = null;
  public battery: ScenarioBattery | null = null;

  public eco: ScenarioEco | null = null;
  public sizing: ScenarioSizing | null = null;

   public constructor(obj: Partial<Scenario> = {}) {
    assignToThis(this, obj);

    this.businessModelRef = this.businessModelRef ? BusinessModelRef[this.businessModelRef] : null;
    this.injectionModelRef = this.injectionModelRef ? InjectionModelRef[this.injectionModelRef] : null;

    this.load = this.load ? new ScenarioLoad(this.load) : null;
    this.pv = this.pv ? new ScenarioPV(this.pv) : null;
    this.grid = this.grid ? new ScenarioGrid(this.grid) : null;
    this.generator = this.generator ? new ScenarioGenerator(this.generator) : null;
    this.battery = this.battery ? new ScenarioBattery(this.battery) : null;

    this.eco = this.eco ? new ScenarioEco(this.eco) : null;
    this.sizing = this.sizing ? new ScenarioSizing(this.sizing) : null;
  }

}
